import * as React from 'react';
import useSWR from 'swr';
import { addDays, subDays, addBusinessDays } from 'date-fns';
import { Place, LocationPlaces } from './shared';

export type DeliveryType = 'economy' | 'standard' | 'express';

export type Countries = {
  [country: string]: {
    zoneZips: { [zone: string]: string[] };
    zoneLeadTimes: { [zone: string]: { [key in DeliveryType]: string } };
    prices: {
      zones: string[];
      space: { [volume: string]: number[] };
    };
  };
};

const {
  REACT_APP_DELIVERY_PRICE_MULTIPLIER_ECONOMY,
  REACT_APP_DELIVERY_PRICE_MULTIPLIER_STANDARD,
  REACT_APP_DELIVERY_PRICE_MULTIPLIER_EXPRESS,
} = process.env;

const MULTIPLIER_ECONOMY =
  Number(REACT_APP_DELIVERY_PRICE_MULTIPLIER_ECONOMY) ?? 1;
const MULTIPLIER_STANDARD =
  Number(REACT_APP_DELIVERY_PRICE_MULTIPLIER_STANDARD) ?? 1;
const MULTIPLIER_EXPRESS =
  Number(REACT_APP_DELIVERY_PRICE_MULTIPLIER_EXPRESS) ?? 1;

export function useDeliveryValues({
  volume,
  location,
  pickupDate,
}: {
  volume?: number;
  location?: LocationPlaces;
  pickupDate?: Date;
}) {
  const { data: countries } = useSWR<Countries>('/api/country-data');
  let fromPrice: number | undefined;
  let toPrice: number | undefined;
  let totalPrice: number | undefined;

  if (countries && location?.from && location?.to && volume) {
    fromPrice = getPrice(countries, location.from, volume);
    toPrice = getPrice(countries, location.to, volume);
  }

  if (fromPrice !== undefined && toPrice !== undefined) {
    totalPrice = fromPrice + toPrice;
  }

  const dates = React.useMemo(() => {
    if (!location) return;
    const leadTimes = countries && getMaxLeadTimes(countries, location);
    return leadTimes && getDeliveryDates(leadTimes, pickupDate);
  }, [location, countries, pickupDate]);

  const prices = React.useMemo(() => {
    return totalPrice
      ? {
          economy: totalPrice * MULTIPLIER_ECONOMY,
          standard: totalPrice * MULTIPLIER_STANDARD,
          express: totalPrice * MULTIPLIER_EXPRESS,
        }
      : undefined;
  }, [totalPrice]);

  return {
    dates,
    prices,
  };
}

function getZone(countries: Countries, place: Place) {
  const countryCode = place.countryCode.toLowerCase();
  const data = countries[countryCode];
  return Object.keys(data.zoneZips).find(zone => {
    return data.zoneZips[zone].some(zip => place.postalCode.startsWith(zip));
  });
}

function getMaxLeadTimes(countries: Countries, location: LocationPlaces) {
  const { from, to } = location;
  const fromLeadTimes = from && getPlaceLeadTimes(countries, from);
  const toLeadTimes = to && getPlaceLeadTimes(countries, to);
  if (!fromLeadTimes || !toLeadTimes) return;

  return fromLeadTimes.standard > toLeadTimes.standard
    ? fromLeadTimes
    : toLeadTimes;
}

function getPlaceLeadTimes(countries: Countries, place: Place) {
  const zone = countries && getZone(countries, place);

  if (zone) {
    const countryCode = place.countryCode.toLowerCase();
    return countries[countryCode].zoneLeadTimes[zone];
  }
}

type DeliveryDates<T> = { [key in keyof T]: Date };

function getDeliveryDates<T>(
  leadTimes: T,
  pickupDate?: Date
): DeliveryDates<T> {
  const date = pickupDate || addDays(new Date(), 6);

  return Object.keys(leadTimes).reduce((acc, delivery) => {
    const key = delivery as keyof T;
    const value = (leadTimes[key] as unknown) as string;
    const leadTime = parseInt(value, 10);
    const leadDate = addDays(date, leadTime / 24);

    return {
      ...acc,
      [delivery]:
        delivery !== 'express' ? getBusinessDayDate(leadDate) : leadDate,
    };
  }, {}) as DeliveryDates<T>;
}

function getPrice(countries: Countries, place: Place, volume: number) {
  const zone = getZone(countries, place);
  const countryPrices = countries[place.countryCode.toLowerCase()].prices;
  const index = countryPrices.zones.findIndex(priceZone => priceZone === zone);
  let price;

  for (let rowLdm in countryPrices.space) {
    if ((rowLdm && rowLdm === 'FTL') || parseInt(rowLdm, 10) >= volume) {
      price = countryPrices.space[rowLdm][index];
      break;
    }
  }

  return price;
}

function getBusinessDayDate(date: Date) {
  const yesterday = subDays(date, 1);
  return addBusinessDays(yesterday, 1);
}
